// src/components/BusinessIncome.js
import React, { useState, useEffect } from 'react';
import { Briefcase, PlusCircle, Trash2, HelpCircle, Edit2, RefreshCw } from 'react-feather';

function BusinessIncome({ scheduleCData = [], onUpdate, formData, onDataChange }) {
  const [activeBizIndex, setActiveBizIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('Income'); 
  
  // --- FIX 1: Read from formData, not local state ---
  // If formData is undefined (loading), default to false
  const showDetails = formData?.showScheduleCDetails === true;

  // Ensure we always have a valid object to avoid crashes
  const currentBiz = scheduleCData[activeBizIndex] || {};
  const seSettings = formData?.scheduleSE || {};

  // --- Helpers ---
  const handleSEChange = (field, value) => {
    const syntheticEvent = {
        target: { name: `scheduleSE.${field}`, value: value, type: typeof value === 'boolean' ? 'checkbox' : 'text', checked: true ? value : undefined }
    };
    onDataChange(syntheticEvent);
  };

  const updateBiz = (field, value) => {
    const newData = [...scheduleCData];
    if (!newData[activeBizIndex]) return;
    
    newData[activeBizIndex] = { ...newData[activeBizIndex], [field]: value };
    onUpdate(newData);
  };

  const updateNested = (section, field, value) => {
    const newData = [...scheduleCData];
    if (!newData[activeBizIndex]) return;

    const sectionData = { ...(newData[activeBizIndex][section] || {}), [field]: value };
    newData[activeBizIndex] = { ...newData[activeBizIndex], [section]: sectionData };
    onUpdate(newData);
  };

  const addBusiness = () => {
    const newBiz = {
        id: `biz-${Date.now()}`,
        businessName: 'New Business',
        principalBusiness: '',
        code: '',
        ein: '',
        accountingMethod: 'Cash',
        grossReceipts: 0,
        returnsAndAllowances: 0,
        otherIncome: 0,
        advertising: 0,
        carAndTruckExpenses: 0,
        commissions: 0,
        contractLabor: 0,
        depletion: 0,
        depreciation: 0,
        employeeBenefitPrograms: 0,
        insurance: 0,
        mortgageInterest: 0,
        otherInterest: 0,
        legalAndProfessional: 0,
        officeExpense: 0,
        pensionAndProfitSharing: 0,
        rentVehiclesMachinery: 0,
        rentOtherProperty: 0,
        repairsAndMaintenance: 0,
        supplies: 0,
        taxesAndLicenses: 0,
        travel: 0,
        meals: 0,
        utilities: 0,
        wages: 0,
        energyEfficientCommercialBuildingsDeduction: 0,
        otherExpenses: [],
        vehicleInfo: { businessMiles: 0, commutingMiles: 0, otherMiles: 0, hasEvidence: false },
        homeOffice: { areaUsed: 0, totalArea: 0 }
    };
    
    onUpdate([...scheduleCData, newBiz]);
    setActiveBizIndex(scheduleCData.length); 
    
    // --- FIX 2: Force "Yes" when adding a business ---
    onDataChange({ target: { name: 'showScheduleCDetails', value: true } });
  };

  const removeBusiness = (e, index) => {
    e.stopPropagation();
    if (scheduleCData.length <= 1) {
        const resetBiz = [{ ...scheduleCData[0], businessName: 'Business 1', grossReceipts: 0, advertising: 0 }];
        onUpdate(resetBiz);
        return;
    }
    const newData = scheduleCData.filter((_, i) => i !== index);
    onUpdate(newData);
    setActiveBizIndex(0);
  };

  const calculateNetProfit = (biz) => {
    if (!biz) return 0;
    const grossReceipts = parseFloat(biz.grossReceipts) || 0;
    const returns = parseFloat(biz.returnsAndAllowances) || 0;
    const otherInc = parseFloat(biz.otherIncome) || 0;
    
    const inventoryStart = parseFloat(biz.inventoryBeginning) || 0;
    const purchases = parseFloat(biz.purchases) || 0;
    const labor = parseFloat(biz.costOfLabor) || 0;
    const materials = parseFloat(biz.materialsAndSupplies) || 0;
    const otherCosts = parseFloat(biz.otherCosts) || 0; 
    const inventoryEnd = parseFloat(biz.inventoryEnd) || 0;
    
    const cogs = (inventoryStart + purchases + labor + materials + otherCosts) - inventoryEnd;
    const grossIncome = (grossReceipts - returns) - cogs + otherInc;

    let totalExpenses = 0;
    const expenseFields = [
        'advertising', 'carAndTruckExpenses', 'commissions', 'contractLabor', 'depletion', 
        'depreciation', 'employeeBenefitPrograms', 'insurance', 'mortgageInterest', 
        'otherInterest', 'legalAndProfessional', 'officeExpense', 'pensionAndProfitSharing', 
        'rentVehiclesMachinery', 'rentOtherProperty', 'repairsAndMaintenance', 'supplies', 
        'taxesAndLicenses', 'travel', 'utilities', 'wages', 'energyEfficientCommercialBuildingsDeduction'
    ];
    expenseFields.forEach(f => totalExpenses += (parseFloat(biz[f]) || 0));
    totalExpenses += (parseFloat(biz.meals) || 0) * 0.50;
    (biz.otherExpenses || []).forEach(e => totalExpenses += (parseFloat(e.amount) || 0));

    const tentativeProfit = grossIncome - totalExpenses;
    const homeArea = parseFloat(biz.homeOffice?.areaUsed) || 0;
    const calculatedHomeDed = Math.min(homeArea, 300) * 5;
    const allowedHomeDed = Math.max(0, Math.min(calculatedHomeDed, tentativeProfit));

    return tentativeProfit - allowedHomeDed;
  };

  const calculatedTotalProfit = scheduleCData.reduce((sum, biz) => sum + calculateNetProfit(biz), 0);
  
  const isManualOverrideActive = formData?.manualBusinessIncome !== '' && formData?.manualBusinessIncome !== null && !isNaN(parseFloat(formData?.manualBusinessIncome));

  const clearManualOverride = () => {
      onDataChange({ target: { name: 'manualBusinessIncome', value: '' } });
  };

  return (
    <div className="form-section" style={{ marginTop: '2rem' }}>
      <div className="content-box-header">
        <h2><Briefcase size={20} className="icon-orange" /> Profit or Loss From Business</h2>
      </div>

      {/* --- QUESTION CARD --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label style={{ fontWeight: 600, color: '#554833ff' }}>Do you need to complete the detailed Schedule C?</label>
          <p style={{ fontSize: '0.9rem', color: '#8b7f64ff', marginBottom: '0.75rem' }}>
            Answer <strong>Yes</strong> if you want to itemize expenses (like advertising, vehicle mileage, or home office) to reduce your tax. Answer <strong>No</strong> if you just want to enter your final Net Profit.
          </p>
          <div className="radio-group">
            <input 
              type="radio" 
              id="schedC_simple" 
              checked={!showDetails} 
              // --- FIX 3: Send simple value object, NO 'checkbox' type ---
              onChange={() => onDataChange({ target: { name: 'showScheduleCDetails', value: false } })} 
            />
            <label htmlFor="schedC_simple">No (Enter Net Profit Only)</label>

            <input 
              type="radio" 
              id="schedC_detailed" 
              checked={showDetails} 
              // --- FIX 3: Send simple value object, NO 'checkbox' type ---
              onChange={() => onDataChange({ target: { name: 'showScheduleCDetails', value: true } })} 
            />
            <label htmlFor="schedC_detailed">Yes (Itemize Income & Expenses)</label>
          </div>
        </div>
      </div>

      {/* --- SIMPLE VIEW (Manual Override Only) --- */}
      {!showDetails && (
        <div className="dependent-card" style={{ animation: 'fadeIn 0.3s' }}>
            <div className="form-group">
                <label style={{ fontWeight: 600, color: '#554533ff' }}>Net Business Income (Schedule 1, Line 3)</label>
                <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input 
                        type="number" 
                        name="manualBusinessIncome" 
                        value={formData?.manualBusinessIncome ?? ''} 
                        onChange={onDataChange} 
                        placeholder="0" 
                    />
                </div>
                <small>Enter the final Net Profit (or Loss) from your self-employment activity.</small>
            </div>
        </div>
      )}

      {/* --- DETAILED VIEW (Full Form) --- */}
      {showDetails && (
        <div style={{ animation: 'fadeIn 0.3s' }}>
            
            {/* Summary Header for Detailed View */}
            <div className="dependent-card" style={{ marginBottom: '1.5rem', border: '2px solid var(--accent-primary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Edit2 size={16} className="icon-blue" />
                    <label style={{ fontWeight: 600, color: '#554a33ff', margin: 0 }}>Net Profit Summary</label>
                </div>
                
                <div className="form-row" style={{ alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label>Calculated from Details Below</label>
                        <input 
                            type="text" 
                            value={`$${calculatedTotalProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
                            readOnly 
                            style={{ backgroundColor: '#f9f5f1ff', color: '#8b7a64ff', fontWeight: 'bold' }} 
                        />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label style={{ color: '#9a3412' }}>Manual Override (Optional)</label>
                        <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input 
                                type="number" 
                                name="manualBusinessIncome" 
                                value={formData?.manualBusinessIncome ?? ''} 
                                onChange={onDataChange} 
                                placeholder="0" 
                                style={{ borderColor: isManualOverrideActive ? '#ea580c' : '#fed7aa' }}
                            />
                        </div>
                    </div>
                </div>
                
                {isManualOverrideActive && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <small style={{ color: '#c2410c' }}>
                            <strong>Note:</strong> You have a manual amount entered. The calculator will ignore your itemized expenses below and use the manual amount instead.
                        </small>
                        <button 
                            onClick={clearManualOverride}
                            style={{ background: 'none', border: '1px solid #fdba74', borderRadius: '4px', padding: '4px 8px', color: '#c2410c', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            <RefreshCw size={12} /> Use Calculated
                        </button>
                    </div>
                )}
            </div>

            {/* Business Selector Tabs */}
            <div className="content-box-header" style={{ marginBottom: '0.5rem' }}>
                <h4>Business Details</h4>
                <button onClick={addBusiness} className="add-item-button"><PlusCircle size={16} /> Add Business</button>
            </div>

            <div className="tab-navigation" style={{ marginBottom: '1rem', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '5px' }}>
                {scheduleCData.map((biz, idx) => (
                    <div key={biz.id} style={{ position: 'relative' }}>
                        <button 
                            className={`tab-button ${idx === activeBizIndex ? 'active' : ''}`}
                            onClick={() => setActiveBizIndex(idx)}
                            style={{ paddingRight: scheduleCData.length > 1 ? '30px' : '15px' }}
                        >
                            {biz.businessName || `Business ${idx + 1}`}
                        </button>
                        {scheduleCData.length > 1 && (
                            <button 
                                onClick={(e) => removeBusiness(e, idx)}
                                style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                            >
                                <Trash2 size={12} />
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <div className="dependent-card">
                {/* Internal Navigation */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '1.5rem', borderBottom: '1px solid #f0e8e2ff', paddingBottom: '10px', overflowX: 'auto' }}>
                    {['General', 'Income', 'Expenses', 'Vehicle/Home'].map(tab => (
                        <button 
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                borderBottom: activeTab === tab ? '2px solid #EB8258' : 'none',
                                color: activeTab === tab ? '#EB8258' : '#8b7264ff',
                                fontWeight: activeTab === tab ? 'bold' : 'normal',
                                padding: '5px 10px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* --- TAB: GENERAL --- */}
                {activeTab === 'General' && (
                    <div className="form-section">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Name of Business</label>
                                <input type="text" value={currentBiz.businessName || ''} onChange={e => updateBiz('businessName', e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Principal Business/Profession</label>
                                <input type="text" value={currentBiz.principalBusiness || ''} onChange={e => updateBiz('principalBusiness', e.target.value)} placeholder="e.g. Web Design" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <div className="header-title-with-help">
                                    <label>NAICS Code</label>
                                    <div className="has-tooltip header-help-icon" data-tooltip="See instructions for list of codes.">
                                        <HelpCircle size={16} />
                                    </div>
                                </div>
                                <input type="text" value={currentBiz.code || ''} onChange={e => updateBiz('code', e.target.value)} placeholder="6-digit code" />
                            </div>
                            <div className="form-group">
                                <label>Employer ID (EIN)</label>
                                <input type="text" value={currentBiz.ein || ''} onChange={e => updateBiz('ein', e.target.value)} placeholder="Leave blank if none" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Business Street Address</label>
                            <input type="text" value={currentBiz.businessAddress || ''} onChange={e => updateBiz('businessAddress', e.target.value)} placeholder="Street address, suite number" />
                        </div>
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label>City</label>
                                <input type="text" value={currentBiz.businessCity || ''} onChange={e => updateBiz('businessCity', e.target.value)} />
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>State</label>
                                <input type="text" value={currentBiz.businessState || ''} onChange={e => updateBiz('businessState', e.target.value)} maxLength={2} placeholder="CA" />
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Zip Code</label>
                                <input type="text" value={currentBiz.businessZip || ''} onChange={e => updateBiz('businessZip', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label>Accounting Method</label>
                            <div className="radio-group">
                                <input type="radio" id="accCash" name="accountingMethod" value="Cash" checked={currentBiz.accountingMethod === 'Cash'} onChange={e => updateBiz('accountingMethod', e.target.value)} />
                                <label htmlFor="accCash">Cash</label>
                                <input type="radio" id="accAccrual" name="accountingMethod" value="Accrual" checked={currentBiz.accountingMethod === 'Accrual'} onChange={e => updateBiz('accountingMethod', e.target.value)} />
                                <label htmlFor="accAccrual">Accrual</label>
                                <input type="radio" id="accOther" name="accountingMethod" value="Other" checked={currentBiz.accountingMethod === 'Other'} onChange={e => updateBiz('accountingMethod', e.target.value)} />
                                <label htmlFor="accOther">Other</label>
                            </div>
                        </div>
                        {currentBiz.accountingMethod === 'Other' && (
                            <div className="form-group" style={{ marginTop: '0.5rem', marginLeft: '1rem', borderLeft: '3px solid #f0e9e2ff', paddingLeft: '1rem' }}>
                                <label>Specify Other Method</label>
                                <input type="text" value={currentBiz.accountingMethodOtherDescription || ''} onChange={e => updateBiz('accountingMethodOtherDescription', e.target.value)} placeholder="e.g. Hybrid" />
                            </div>
                        )}
                        <div className="form-row">
                            <div className="form-group checkbox-group">
                                <input type="checkbox" checked={currentBiz.materiallyParticipated || false} onChange={e => updateBiz('materiallyParticipated', e.target.checked)} />
                                <label>Did you materially participate in the operation of this business?</label>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group checkbox-group">
                                <input type="checkbox" checked={currentBiz.startedIn2024 || false} onChange={e => updateBiz('startedIn2024', e.target.checked)} />
                                <label>Did you start or acquire this business in 2024?</label>
                            </div>
                        </div>
                        <div className="form-group" style={{ marginTop: '1rem', borderTop: '1px dashed #ccc', paddingTop: '1rem' }}>
                            <label style={{ fontWeight: '600' }}>1099 Filing Requirements (Lines I & J)</label>
                            <div className="form-group checkbox-group">
                                <input type="checkbox" checked={currentBiz.requiredToFile1099 || false} onChange={e => updateBiz('requiredToFile1099', e.target.checked)} />
                                <label>Did you make any payments in 2024 that would require you to file Form(s) 1099?</label>
                            </div>
                            {currentBiz.requiredToFile1099 && (
                                <div className="form-group checkbox-group" style={{ marginLeft: '20px' }}>
                                    <input type="checkbox" checked={currentBiz.didFile1099 || false} onChange={e => updateBiz('didFile1099', e.target.checked)} />
                                    <label>If "Yes," did you or will you file required Form(s) 1099?</label>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* --- TAB: INCOME --- */}
                {activeTab === 'Income' && (
                    <div className="form-section">
                        <div className="form-group checkbox-group" style={{ marginBottom: '1rem' }}>
                            <input type="checkbox" checked={currentBiz.statutoryEmployee || false} onChange={e => updateBiz('statutoryEmployee', e.target.checked)} />
                            <label>Check if this income was reported to you on Form W-2 and the "Statutory employee" box on that form was checked.</label>
                        </div>
                        <div className="form-group">
                            <label>Gross Receipts or Sales (Line 1)</label>
                            <input type="number" value={currentBiz.grossReceipts ?? ''} onChange={e => updateBiz('grossReceipts', e.target.value)} />
                            <small>Include amounts from 1099-NEC, 1099-MISC, 1099-K, and cash.</small>
                        </div>
                        <div className="form-group">
                            <label>Returns and Allowances (Line 2)</label>
                            <input type="number" value={currentBiz.returnsAndAllowances ?? ''} onChange={e => updateBiz('returnsAndAllowances', e.target.value)} />
                            <small>Refunds you gave to customers.</small>
                        </div>
                        
                        <div className="form-group">
                            <label>Other Income (Line 6)</label>
                            <input type="number" value={currentBiz.otherIncome ?? ''} onChange={e => updateBiz('otherIncome', e.target.value)} />
                            <small>Include federal/state gasoline or fuel tax credit or refund.</small>
                        </div>
                        
                        <h5 style={{ marginTop: '1.5rem', borderTop: '1px dashed #ccc', paddingTop: '1rem', marginBottom: '1rem' }}>Cost of Goods Sold (Part III)</h5>
                        
                        <div className="form-group" style={{ backgroundColor: '#fcfbf8ff', padding: '1.25rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                            <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', color: '#554133ff' }}>Method used to value closing inventory (Line 33)</label>
                            <div className="radio-group">
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: '1.5rem', marginBottom: '0.5rem' }}>
                                    <input type="radio" id="methodCost" name="inventoryMethod" value="Cost" checked={currentBiz.inventoryMethod === 'Cost'} onChange={e => updateBiz('inventoryMethod', e.target.value)} />
                                    <label htmlFor="methodCost" style={{ marginLeft: '0.5rem', marginRight: 0 }}>Cost</label>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: '1.5rem', marginBottom: '0.5rem' }}>
                                    <input type="radio" id="methodLower" name="inventoryMethod" value="Lower" checked={currentBiz.inventoryMethod === 'Lower'} onChange={e => updateBiz('inventoryMethod', e.target.value)} />
                                    <label htmlFor="methodLower" style={{ marginLeft: '0.5rem', marginRight: 0 }}>Lower of cost or market</label>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <input type="radio" id="methodOther" name="inventoryMethod" value="Other" checked={currentBiz.inventoryMethod === 'Other'} onChange={e => updateBiz('inventoryMethod', e.target.value)} />
                                    <label htmlFor="methodOther" style={{ marginLeft: '0.5rem', marginRight: 0 }}>Other (attach explanation)</label>
                                </div>
                            </div>
                        </div>

                        <div className="form-group" style={{ backgroundColor: '#fcfaf8ff', padding: '1.25rem', borderRadius: '0.5rem', border: '1px solid #f0e9e2ff', marginBottom: '1.5rem' }}>
                            <div className="checkbox-group" style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <input type="checkbox" id="inventoryChange" checked={currentBiz.inventoryChange || false} onChange={e => updateBiz('inventoryChange', e.target.checked)} style={{ marginTop: '0.3rem' }} />
                                <label htmlFor="inventoryChange" style={{ fontWeight: '600', color: '#554633ff', lineHeight: '1.4' }}>Was there any change in determining quantities, costs, or valuations between opening and closing inventory? (Line 34)</label>
                            </div>
                            {currentBiz.inventoryChange && (
                                <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#fff7ed', borderLeft: '3px solid #ea580c', borderRadius: '4px' }}>
                                    <small style={{ color: '#9a3412', display: 'block', fontSize: '0.9rem' }}><strong>Action Required:</strong> You must attach an explanation for this change to your return.</small>
                                </div>
                            )}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Inventory at Beginning of Year (Line 35)</label>
                                <input type="number" value={currentBiz.inventoryBeginning ?? ''} onChange={e => updateBiz('inventoryBeginning', e.target.value)} />
                                <small style={{ color: '#ea580c' }}>If different from last year's closing inventory, attach explanation.</small>
                            </div>
                            <div className="form-group">
                                <label>Purchases (Less personal use)</label>
                                <input type="number" value={currentBiz.purchases ?? ''} onChange={e => updateBiz('purchases', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Cost of Labor (Line 37)</label>
                                <input type="number" value={currentBiz.costOfLabor ?? ''} onChange={e => updateBiz('costOfLabor', e.target.value)} />
                                <small>Do not include amounts paid to yourself.</small>
                            </div>
                            <div className="form-group">
                                <label>Materials and Supplies (Line 38)</label>
                                <input type="number" value={currentBiz.materialsAndSupplies ?? ''} onChange={e => updateBiz('materialsAndSupplies', e.target.value)} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Other Costs (Line 39)</label>
                            <input type="number" value={currentBiz.otherCosts ?? ''} onChange={e => updateBiz('otherCosts', e.target.value)} />
                            <small>Attach explanation if you enter an amount here.</small>
                        </div>
                        <div className="form-group">
                            <label>Inventory at End of Year (Line 41)</label>
                            <input type="number" value={currentBiz.inventoryEnd ?? ''} onChange={e => updateBiz('inventoryEnd', e.target.value)} />
                        </div>

                        <h5 style={{ marginTop: '1.5rem', borderTop: '1px dashed #ccc', paddingTop: '1rem' }}>Self-Employment Tax Adjustments (Schedule SE)</h5>
                        <div className="form-group checkbox-group" style={{ marginBottom: '1rem', backgroundColor: '#fcfaf8ff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            <input type="checkbox" id="minister4361" checked={seSettings.ministerFiledForm4361 || false} onChange={(e) => handleSEChange('ministerFiledForm4361', e.target.checked)} />
                            <label htmlFor="minister4361" style={{ fontSize: '0.9rem' }}><strong>Line A:</strong> I am a minister, member of a religious order, or Christian Science practitioner and I filed <strong>Form 4361</strong> (Exemption from SE Tax).</label>
                        </div>
                        <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fff7ed', border: '1px solid #ffedd5', borderRadius: '0.375rem' }}>
                            <small style={{ color: '#9a3412', display: 'block', lineHeight: '1.4' }}><strong>Note on Farm Income:</strong> This app does not support Schedule F. <strong>Line 1a</strong> (Net farm profit) on the generated Schedule SE will default to <strong>$0.00</strong>. If you have farm income, you can manually write it on the printed PDF.</small>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Exempt Notary Income</label>
                                <input type="number" value={currentBiz.exemptNotaryIncome ?? ''} onChange={e => updateBiz('exemptNotaryIncome', e.target.value)} />
                                <small>Fees received as a Notary Public are exempt from SE Tax.</small>
                            </div>
                            <div className="form-group">
                                <label>Conservation Reserve Program (CRP)</label>
                                <input type="number" value={currentBiz.conservationReserveProgramPayments ?? ''} onChange={e => updateBiz('conservationReserveProgramPayments', e.target.value)} />
                                <small>Enter amount included in Schedule C if receiving Social Security.</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- TAB: EXPENSES --- */}
                {activeTab === 'Expenses' && (
                    <div className="form-section">
                        <div className="form-row">
                            <div className="form-group"><label>Advertising (Line 8)</label><input type="number" value={currentBiz.advertising ?? ''} onChange={e => updateBiz('advertising', e.target.value)} /></div>
                            <div className="form-group"><label>Car & Truck Expenses (Line 9)</label><input type="number" value={currentBiz.carAndTruckExpenses ?? ''} onChange={e => updateBiz('carAndTruckExpenses', e.target.value)} /><small>Enter actual expenses here OR use standard mileage in the Vehicle tab.</small></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Commissions and Fees (Line 10)</label><input type="number" value={currentBiz.commissions ?? ''} onChange={e => updateBiz('commissions', e.target.value)} /></div>
                            <div className="form-group"><label>Contract Labor (Line 11)</label><input type="number" value={currentBiz.contractLabor ?? ''} onChange={e => updateBiz('contractLabor', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Depletion (Line 12)</label><input type="number" value={currentBiz.depletion ?? ''} onChange={e => updateBiz('depletion', e.target.value)} /></div>
                            <div className="form-group"><label>Depreciation (Line 13)</label><input type="number" value={currentBiz.depreciation ?? ''} onChange={e => updateBiz('depreciation', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Employee Benefit Programs (Line 14)</label><input type="number" value={currentBiz.employeeBenefitPrograms ?? ''} onChange={e => updateBiz('employeeBenefitPrograms', e.target.value)} /><small>Health insurance, etc. for employees. <strong>Exclude</strong> pension/profit-sharing.</small></div>
                            <div className="form-group"><label>Insurance (other than health) (Line 15)</label><input type="number" value={currentBiz.insurance ?? ''} onChange={e => updateBiz('insurance', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Mortgage Interest (Line 16a)</label><input type="number" value={currentBiz.mortgageInterest ?? ''} onChange={e => updateBiz('mortgageInterest', e.target.value)} /><small>Interest paid to banks/financial institutions.</small></div>
                            <div className="form-group"><label>Other Interest (Line 16b)</label><input type="number" value={currentBiz.otherInterest ?? ''} onChange={e => updateBiz('otherInterest', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Legal & Professional Services (Line 17)</label><input type="number" value={currentBiz.legalAndProfessional ?? ''} onChange={e => updateBiz('legalAndProfessional', e.target.value)} /></div>
                            <div className="form-group"><label>Office Expense (Line 18)</label><input type="number" value={currentBiz.officeExpense ?? ''} onChange={e => updateBiz('officeExpense', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Pension & Profit-Sharing (Line 19)</label><input type="number" value={currentBiz.pensionAndProfitSharing ?? ''} onChange={e => updateBiz('pensionAndProfitSharing', e.target.value)} /><small>Contributions for employees only.</small></div>
                            <div className="form-group"><label>Repairs and Maintenance (Line 21)</label><input type="number" value={currentBiz.repairsAndMaintenance ?? ''} onChange={e => updateBiz('repairsAndMaintenance', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Rent: Vehicles/Machinery (Line 20a)</label><input type="number" value={currentBiz.rentVehicles ?? ''} onChange={e => updateBiz('rentVehicles', e.target.value)} /></div>
                            <div className="form-group"><label>Rent: Other Business Property (Line 20b)</label><input type="number" value={currentBiz.rentMachinery ?? ''} onChange={e => updateBiz('rentMachinery', e.target.value)} placeholder="e.g. Office space" /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Supplies (Line 22)</label><input type="number" value={currentBiz.supplies ?? ''} onChange={e => updateBiz('supplies', e.target.value)} /><small>Only list supplies not included in COGS(Part III).</small></div>
                            <div className="form-group"><label>Taxes and Licenses (Line 23)</label><input type="number" value={currentBiz.taxesAndLicenses ?? ''} onChange={e => updateBiz('taxesAndLicenses', e.target.value)} /></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Travel (Line 24a)</label><input type="number" value={currentBiz.travel ?? ''} onChange={e => updateBiz('travel', e.target.value)} /></div>
                            <div className="form-group"><label>Deductible Meals (50%) (Line 24b)</label><input type="number" value={currentBiz.meals ?? ''} onChange={e => updateBiz('meals', e.target.value)} /><small>Enter total. We calculate the 50% limit.</small></div>
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Utilities (Line 25)</label><input type="number" value={currentBiz.utilities ?? ''} onChange={e => updateBiz('utilities', e.target.value)} /></div>
                            <div className="form-group"><label>Wages (Line 26)</label><input type="number" value={currentBiz.wages ?? ''} onChange={e => updateBiz('wages', e.target.value)} /><small>Less employment credits.</small></div>
                            <div className="form-group">
                                <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                                    <label>Energy Efficient Buildings (Line 27b)</label>
                                    <div className="has-tooltip header-help-icon" data-tooltip="Deduction for energy efficient commercial building property placed in service during the tax year.">
                                        <HelpCircle size={16} />
                                    </div>
                                </div>
                                <input 
                                    type="number" 
                                    value={currentBiz.energyEfficientCommercialBuildingsDeduction ?? ''} 
                                    onChange={e => updateBiz('energyEfficientCommercialBuildingsDeduction', e.target.value)} 
                                    placeholder="0.00"
                                />
                                <small style={{ color: '#ea580c', display: 'block', marginTop: '5px', lineHeight: '1.4' }}><strong>Action Required:</strong> You must manually fill out and attach <strong>Form 7205</strong> to your return. This app does not generate that specific form.</small>
                            </div>
                        </div>
                        
                        {/* --- Other Expenses Dynamic List --- */}
                        <div style={{ marginTop: '1rem', borderTop: '1px dashed #ccc', paddingTop: '1rem' }}>
                            <label>Other Expenses (Part V)</label>
                            <small style={{ display: 'block', color: '#8b7b64ff', marginBottom: '0.5rem' }}>Include only expenses not deducted elsewhere (Lines 8-26, 27b, or Line 30).</small>
                            {(currentBiz.otherExpenses || []).map((exp, idx) => (
                                <div key={idx} className="form-row" style={{ marginBottom: '5px' }}>
                                    <input 
                                        type="text" 
                                        placeholder="Description" 
                                        value={exp.description || ''} 
                                        onChange={e => {
                                            const newExps = [...currentBiz.otherExpenses];
                                            newExps[idx].description = e.target.value;
                                            updateBiz('otherExpenses', newExps);
                                        }}
                                        style={{ flex: 2 }}
                                    />
                                    <input 
                                        type="number" 
                                        placeholder="Amount" 
                                        value={exp.amount ?? ''} 
                                        onChange={e => {
                                            const newExps = [...currentBiz.otherExpenses];
                                            newExps[idx].amount = e.target.value;
                                            updateBiz('otherExpenses', newExps);
                                        }}
                                        style={{ flex: 1 }}
                                    />
                                    <button onClick={() => {
                                        const newExps = currentBiz.otherExpenses.filter((_, i) => i !== idx);
                                        updateBiz('otherExpenses', newExps);
                                    }} className="delete-card-button"><Trash2 size={16}/></button>
                                </div>
                            ))}
                            <button 
                                onClick={() => updateBiz('otherExpenses', [...(currentBiz.otherExpenses || []), { description: '', amount: '' }])}
                                className="add-item-button" 
                                style={{ marginTop: '5px', fontSize: '0.8rem' }}
                            >
                                <PlusCircle size={14} /> Add Other Expense
                            </button>
                        </div>
                    </div>
                )}

                {/* --- TAB: VEHICLE / HOME --- */}
                {activeTab === 'Vehicle/Home' && (
                    <div className="form-section">
                        <h5>Vehicle Information (Part IV)</h5>
                        <div className="form-group">
                            <label>Date vehicle placed in service (Line 43)</label>
                            <input type="date" value={currentBiz.vehicleInfo?.inServiceDate || ''} onChange={e => updateNested('vehicleInfo', 'inServiceDate', e.target.value)} />
                        </div>
                        <div className="form-row">
                            <div className="form-group"><label>Business Miles</label><input type="number" value={currentBiz.vehicleInfo?.businessMiles ?? ''} onChange={e => updateNested('vehicleInfo', 'businessMiles', e.target.value)} /></div>
                            <div className="form-group"><label>Commuting Miles</label><input type="number" value={currentBiz.vehicleInfo?.commutingMiles ?? ''} onChange={e => updateNested('vehicleInfo', 'commutingMiles', e.target.value)} /></div>
                            <div className="form-group"><label>Other Miles (Line 44c)</label><input type="number" value={currentBiz.vehicleInfo?.otherMiles ?? ''} onChange={e => updateNested('vehicleInfo', 'otherMiles', e.target.value)} placeholder="Personal/Errands" /></div>
                        </div>
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label>Was your vehicle available for personal use during off-duty hours? (Line 45)</label>
                            <div className="radio-group">
                                <input type="radio" id="personalUseYes" checked={currentBiz.vehicleInfo?.availableForPersonalUse === true} onChange={() => updateNested('vehicleInfo', 'availableForPersonalUse', true)} />
                                <label htmlFor="personalUseYes">Yes</label>
                                <input type="radio" id="personalUseNo" checked={currentBiz.vehicleInfo?.availableForPersonalUse === false} onChange={() => updateNested('vehicleInfo', 'availableForPersonalUse', false)} />
                                <label htmlFor="personalUseNo">No</label>
                            </div>
                        </div>
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label>Do you (or your spouse) have another vehicle available for personal use? (Line 46)</label>
                            <div className="radio-group">
                                <input type="radio" id="otherVehicleYes" checked={currentBiz.vehicleInfo?.anotherVehicleAvailable === true} onChange={() => updateNested('vehicleInfo', 'anotherVehicleAvailable', true)} />
                                <label htmlFor="otherVehicleYes">Yes</label>
                                <input type="radio" id="otherVehicleNo" checked={currentBiz.vehicleInfo?.anotherVehicleAvailable === false} onChange={() => updateNested('vehicleInfo', 'anotherVehicleAvailable', false)} />
                                <label htmlFor="otherVehicleNo">No</label>
                            </div>
                        </div>
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label>Do you have evidence to support your deduction? (Line 47a)</label>
                            <div className="radio-group">
                                <input type="radio" id="evidenceYes" checked={currentBiz.vehicleInfo?.hasEvidence === true} onChange={() => updateNested('vehicleInfo', 'hasEvidence', true)} />
                                <label htmlFor="evidenceYes">Yes</label>
                                <input type="radio" id="evidenceNo" checked={currentBiz.vehicleInfo?.hasEvidence === false} onChange={() => updateNested('vehicleInfo', 'hasEvidence', false)} />
                                <label htmlFor="evidenceNo">No</label>
                            </div>
                        </div>
                        {currentBiz.vehicleInfo?.hasEvidence && (
                            <div className="form-group" style={{ marginTop: '1rem', marginLeft: '1.5rem' }}>
                                <label>If "Yes," is the evidence written? (Line 47b)</label>
                                <div className="radio-group">
                                    <input type="radio" id="writtenYes" checked={currentBiz.vehicleInfo?.isEvidenceWritten === true} onChange={() => updateNested('vehicleInfo', 'isEvidenceWritten', true)} />
                                    <label htmlFor="writtenYes">Yes</label>
                                    <input type="radio" id="writtenNo" checked={currentBiz.vehicleInfo?.isEvidenceWritten === false} onChange={() => updateNested('vehicleInfo', 'isEvidenceWritten', false)} />
                                    <label htmlFor="writtenNo">No</label>
                                </div>
                            </div>
                        )}
                        <h5 style={{ marginTop: '1.5rem', borderTop: '1px dashed #ccc', paddingTop: '1rem' }}>Business Use of Home (Simplified Method)</h5>
                        <div className="form-row">
                            <div className="form-group"><label>Area used exclusively for business (sq ft)</label><input type="number" value={currentBiz.homeOffice?.areaUsed ?? ''} onChange={e => updateNested('homeOffice', 'areaUsed', e.target.value)} /><small>Max 300 sq ft for simplified method.</small></div>
                            <div className="form-group"><label>Total area of home (sq ft)</label><input type="number" value={currentBiz.homeOffice?.totalArea ?? ''} onChange={e => updateNested('homeOffice', 'totalArea', e.target.value)} /></div>
                        </div>
                    </div>
                )}

                {/* --- NET PROFIT DISPLAY --- */}
                <div className="totals-summary-box" style={{ marginTop: '2rem', backgroundColor: '#f0fdf4', borderColor: '#86efac' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>Net Profit (or Loss):</strong>
                        <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: calculateNetProfit(currentBiz) >= 0 ? '#166534' : '#dc2626' }}>
                            ${calculateNetProfit(currentBiz).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                    <small>This amount flows to Schedule 1, Line 3.</small>
                </div>
                
                {/* --- GLOBAL SCHEDULE SE SETTINGS --- */}
                <div className="dependent-card" style={{ marginTop: '2rem', border: '1px solid #e1d5cbff' }}>
                    <div className="content-box-header" style={{ marginBottom: '1rem', borderBottom: 'none', paddingBottom: 0 }}>
                        <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <HelpCircle size={18} className="icon-blue" /> Optional Methods (Schedule SE Part II)
                        </h4>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#695947ff', marginBottom: '1rem' }}>If your net profit is less than $7,240 (or you have a loss), you may be able to use the <strong>Nonfarm Optional Method</strong> to report $6,440 (or less) as your net earnings. This allows you to receive credit for Social Security coverage even though you had a loss.</p>
                    <div className="form-group checkbox-group" style={{ backgroundColor: '#fff7ed', padding: '10px', borderRadius: '6px' }}>
                        <input type="checkbox" id="useOptional" checked={seSettings.useNonfarmOptionalMethod || false} onChange={(e) => handleSEChange('useNonfarmOptionalMethod', e.target.checked)} />
                        <label htmlFor="useOptional" style={{ fontWeight: '600', color: '#9a3412' }}>I want to use the Nonfarm Optional Method.</label>
                    </div>
                    {seSettings.useNonfarmOptionalMethod && (
                        <div style={{ marginTop: '1rem', marginLeft: '1.5rem', borderLeft: '3px solid #fed7aa', paddingLeft: '1rem' }}>
                            <div className="form-group checkbox-group">
                                <input type="checkbox" id="metTest" checked={seSettings.metSelfEmploymentTest || false} onChange={(e) => handleSEChange('metSelfEmploymentTest', e.target.checked)} />
                                <label htmlFor="metTest">I had net earnings from self-employment of $400 or more in at least 2 of the prior 3 tax years.</label>
                            </div>
                            {!seSettings.metSelfEmploymentTest && <small style={{ color: '#ef4444', display: 'block', marginTop: '5px' }}>You must meet this test to qualify.</small>}
                        </div>
                    )}
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default BusinessIncome;